import { faArrowLeft, faDownload } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	formatBillingDate,
	paymentTypeLabel,
	storefrontInvoiceClosedNote,
} from '@supply-mover-2-0/core';
import {
	displayExternalId,
	InvoiceStatus,
	isInvoiceCreditMemo,
	isInvoiceStatusPayable,
	isPortalCreditTransaction,
} from '@supply-mover-2-0/shared';
import {
	ImagePlaceholder,
	ResilientImage,
	useTranslation,
} from '@supply-mover-2-0/web-ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CardChevron } from '../components/CardChevron';
import {
	creditMemoAvailable,
	CreditMemoStatusPill,
	InvoicePaymentStatusPill,
} from '../components/InvoiceStatusChips';
import { ItemDesignationBadges } from '../components/ItemDesignationBadges';
import { PayInvoiceSidebar } from '../components/PayInvoiceSidebar';
import { DetailPageSkeleton } from '../components/skeletons';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { saveOrShareBlob } from '../lib/downloadFile';
import { formatCurrency } from '../lib/formatCurrency';
import { formatStatementDate } from '../lib/formatStatementDate';
import * as portalInvoiceService from '../services/portalInvoiceService';
import { useLoginModalStore } from '../stores/loginModalStore';

/**
 * Invoice detail page with line items and billing address.
 */
export function InvoiceDetailPage(): JSX.Element {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { token, isAuthenticated } = usePortalAuth();
	const display = useDisplaySettings();
	const queryClient = useQueryClient();
	const openLoginModal = useLoginModalStore((s) => s.open);
	const [showPaySidebar, setShowPaySidebar] = useState(false);
	const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

	const invoiceId = id != null ? parseInt(id, 10) : NaN;

	const { data, isLoading } = useQuery({
		queryKey: ['invoice', invoiceId],
		queryFn: () =>
			portalInvoiceService.getInvoiceDetail(token ?? '', invoiceId),
		enabled: token != null && !isNaN(invoiceId),
	});

	const invoice = data?.data ?? null;

	// Download the rendered invoice PDF (PD-14606). Native shells get the
	// share sheet via saveOrShareBlob; browsers get a normal download.
	const handleDownloadPdf = async (): Promise<void> => {
		if (invoice == null || token == null) {
			return;
		}
		setIsDownloadingPdf(true);
		try {
			const blob = await portalInvoiceService.downloadInvoicePdf(
				token,
				invoice.id,
			);
			await saveOrShareBlob(
				blob,
				`${displayExternalId(invoice, { fallback: 'none' }) ?? `INV-${invoice.id}`}.pdf`,
			);
		} catch (err) {
			console.error('Error downloading invoice PDF:', err);
		} finally {
			setIsDownloadingPdf(false);
		}
	};

	// Prompt login if not authenticated
	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	if (!isAuthenticated) {
		return <div />;
	}

	if (isLoading) {
		// Skeleton shaped like the detail layout (PD-14692); the action-button
		// spinners below (PDF download) are the only spinners left on the page.
		return (
			<div className="max-w-4xl mx-auto px-4 py-4">
				<DetailPageSkeleton />
			</div>
		);
	}

	if (invoice == null) {
		return (
			<div className="max-w-4xl mx-auto px-4 py-12 text-center">
				<p className="text-brand-muted mb-4">{t('Invoice not found.')}</p>
				<button
					type="button"
					onClick={() => navigate('/invoices')}
					className="text-sm text-portal-accent hover:underline"
				>
					{t('Back to invoices')}
				</button>
			</div>
		);
	}

	// A credit memo is `type='2'` or a negative-total row — the same
	// derivation the invoices list uses (PD-14172). It has nothing due and
	// no balance to pay, so those fields are suppressed below (PD-15245).
	const isCreditMemo =
		isInvoiceCreditMemo(invoice.type) ||
		(invoice.total != null && parseFloat(invoice.total) < 0);
	// PD-16381 — why a balance has no Pay button, when its status is the reason.
	const closedToPaymentNote = storefrontInvoiceClosedNote(invoice);
	// PD-16747 — a Closed document is finished, so it shows no balance line at
	// all. The ERP can close an invoice while Workd's stored balance still lags
	// (HF CAP-68895 read "Balance: $84.45" under a Closed pill), and a red
	// balance on a closed invoice tells the customer they owe money they do
	// not. The pill already says Closed.
	const isClosed = invoice.status === (InvoiceStatus.CLOSED as string);

	return (
		<div className="px-4 py-4 max-w-4xl mx-auto">
			{/* Back button + download action share one row: back link left,
			    primary Download PDF right, mirroring the statement detail
			    page's button styling (PD-14240). The back button navigates to
			    the list EXPLICITLY. It used to be navigate(-1), but the
			    previous history entry isn't always the list: after visiting a
			    line item the entry behind this page is the line receipt, so
			    popping landed back ON the line item. The label promises "Back
			    to Invoices" — go there. */}
			<div className="flex items-center justify-between gap-3 mb-4">
				<button
					type="button"
					onClick={() => navigate('/invoices')}
					className="flex items-center gap-2 text-sm text-brand-muted hover:text-default min-h-touch"
				>
					<FontAwesomeIcon icon={faArrowLeft} />
					<span>{t('Back to Invoices')}</span>
				</button>
				<button
					type="button"
					onClick={() => {
						void handleDownloadPdf();
					}}
					disabled={isDownloadingPdf}
					className="px-4 py-2 text-sm bg-portal-accent hover:bg-portal-accent-hover text-white font-medium rounded-lg min-h-touch"
				>
					<FontAwesomeIcon icon={faDownload} className="mr-2" />
					{isDownloadingPdf ? t('Preparing...') : t('Download PDF')}
				</button>
			</div>

			{/* Invoice header */}
			<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
				<div className="flex items-start justify-between gap-3">
					<div>
						<div className="flex items-center gap-2">
							<h1 className="text-lg font-bold text-default">
								{isCreditMemo ? t('Credit Memo') : t('Invoice')} #
								{/* PD-16990 — the customer-facing number first. NetSuite's
								    own id (11081258) is not the number on the invoice the
								    customer holds (INV0327599). */}
								{t(displayExternalId(invoice) ?? '')}
							</h1>
							{/* PD-15441 — this chip was hand-rolled here and knew
							    nothing about credit memos, so an RMA with credit still
							    on it (the ERP stores that balance positive) landed on
							    the final branch and wore a red "Unpaid" — an alarming
							    label for a document that owes the CUSTOMER money. Both
							    document types now use the same shared pills the
							    invoices list uses, so the two surfaces cannot drift
							    apart again. */}
							{isCreditMemo ? (
								<CreditMemoStatusPill invoice={invoice} />
							) : (
								<InvoicePaymentStatusPill invoice={invoice} />
							)}
						</div>
						{invoice.clientName != null && (
							<p className="text-sm text-brand-muted mt-0.5">
								{t(invoice.clientName)}
							</p>
						)}
						<p className="text-sm text-brand-muted mt-1">
							{t('Date')}: {t(invoice.date ?? '')}
							{!isCreditMemo && invoice.dueDate != null && (
								<span className="ml-3">
									{t('Due')}: {t(invoice.dueDate)}
								</span>
							)}
						</p>
						{/* PD-15869 — under cycle billing the terms are not what
						    decides when this document is paid: it joins the
						    account's next cycle charge. Saying "Net 30" there
						    would be actively wrong, so the cycle line replaces
						    it. Every other mode keeps the terms line, unless the
						    storefront hides terms from customers entirely
						    (PD-14898) — the cycle line is not payment terms, so
						    that toggle deliberately leaves it alone. */}
						{invoice.cycleNextDate != null ? (
							<p className="text-xs text-faint mt-0.5">
								{t('Included in your next cycle charge on {{date}}', {
									date: t(formatBillingDate(invoice.cycleNextDate) ?? '—'),
								})}
							</p>
						) : display.showPaymentTerms && invoice.paymentTerm != null ? (
							<p className="text-xs text-faint mt-0.5">
								{t('Terms')}: {t(invoice.paymentTerm)}
							</p>
						) : null}
						{/* PD-15869 — the receivable belongs to the parent account
						    under "Bill with parent"; the customer should not go
						    looking for a payment they never owed. */}
						{invoice.billedToName != null && (
							<p className="text-xs text-faint mt-0.5">
								{t('Billed to {{account}}', {
									account: t(invoice.billedToName),
								})}
							</p>
						)}
					</div>
					<div className="text-right shrink-0">
						<div className="text-xl font-bold text-default">
							{t(formatCurrency(invoice.total) ?? '-')}
						</div>
						{/* PD-15441 — a credit memo shows the credit still available
						    to spend, matching the invoices list. The Pay action stays
						    off it: a memo is money owed TO the customer, so there is
						    nothing to pay here (PD-15245) — it is applied against
						    invoices from the Make a Payment screen instead. */}
						{isCreditMemo && !isClosed && creditMemoAvailable(invoice) > 0 && (
							<div className="text-sm text-success mt-0.5">
								{t('Available')}:{' '}
								{t(formatCurrency(creditMemoAvailable(invoice)) ?? '')}
							</div>
						)}
						{!isCreditMemo &&
							!isClosed &&
							invoice.outstandingBalance != null &&
							parseFloat(invoice.outstandingBalance) > 0 && (
								<>
									<div className="text-sm text-danger mt-0.5">
										{t('Balance')}:{' '}
										{t(formatCurrency(invoice.outstandingBalance) ?? '')}
									</div>
									{/* Pay button hidden when the storefront disables
									    payments OR no gateway is configured (the API
									    coerces canPayInvoices to false in either
									    case). Without this gate the user could open
									    the sidebar, fill it out, and then hit a 503 — a
									    worse experience than no button at all. */}
									{/* PD-16381 — and only while the invoice's status
									    still allows payment: a closed or cancelled
									    invoice is left off Make Payment, so it is not
									    offered here either (the API refuses it too). */}
									{display.canPayInvoices &&
										isInvoiceStatusPayable(invoice.status) && (
											<button
												type="button"
												onClick={() => {
													setShowPaySidebar(true);
												}}
												className="mt-2 px-4 py-1.5 bg-portal-accent hover:bg-portal-accent-hover text-white text-sm font-medium rounded-lg transition-colors"
											>
												{t('Pay Invoice')}
											</button>
										)}
									{/* PD-16381 — in the button's place, say why it is not
									    there, so a balance with no way to pay it does not
									    read as a fault. */}
									{display.canPayInvoices && closedToPaymentNote != null && (
										<p className="text-xs text-faint mt-2">
											{t(closedToPaymentNote)}
										</p>
									)}
								</>
							)}
					</div>
				</div>
			</div>

			{/* Summary card */}
			<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
				<h2 className="text-sm font-semibold text-default mb-3">
					{t('Summary')}
				</h2>
				<dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
					<dt className="text-brand-muted">{t('Subtotal')}</dt>
					<dd className="text-right text-default">
						{t(formatCurrency(invoice.subtotal) ?? '-')}
					</dd>

					{invoice.discount != null && parseFloat(invoice.discount) !== 0 && (
						<>
							<dt className="text-brand-muted">{t('Discount')}</dt>
							<dd className="text-right text-success">
								-{t(formatCurrency(invoice.discount) ?? '')}
							</dd>
						</>
					)}

					{invoice.tax != null && parseFloat(invoice.tax) !== 0 && (
						<>
							<dt className="text-brand-muted">{t('Tax')}</dt>
							<dd className="text-right text-default">
								{t(formatCurrency(invoice.tax) ?? '')}
							</dd>
						</>
					)}

					{invoice.freight != null && parseFloat(invoice.freight) !== 0 && (
						<>
							<dt className="text-brand-muted">{t('Shipping')}</dt>
							<dd className="text-right text-default">
								{t(formatCurrency(invoice.freight) ?? '')}
							</dd>
						</>
					)}

					{/* PD-15553 — the processing fee is part of the total, so the
					    summary names it instead of leaving an unexplained gap
					    between the lines and what was charged. */}
					{invoice.processingFee != null &&
						parseFloat(invoice.processingFee) !== 0 && (
							<>
								<dt className="text-brand-muted">{t('Processing Fee')}</dt>
								<dd className="text-right text-default">
									{t(formatCurrency(invoice.processingFee) ?? '')}
								</dd>
							</>
						)}

					<dt className="text-default font-semibold border-t border-brand-border pt-2">
						{t('Total')}
					</dt>
					<dd className="text-right text-default font-bold border-t border-brand-border pt-2">
						{t(formatCurrency(invoice.total) ?? '-')}
					</dd>
				</dl>
			</div>

			{/* Payments (PD-15553) — the transactions applied to this document,
			    incl. each payment's processing fee. A failed/returned payment
			    stays visible in red: it is why a paid invoice reads as open
			    again, and hiding it turns an ACH return into a support call. */}
			{invoice.payments.length > 0 && (
				<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
					<h2 className="text-sm font-semibold text-default mb-1">
						{t('Payments')}
					</h2>
					<div className="divide-y divide-subtle">
						{invoice.payments.map((payment, index) => {
							const isCredit = isPortalCreditTransaction(payment.type);
							const isFailed = payment.displayStatus === 'Failed';
							return (
								<div
									key={`${payment.date ?? 'payment'}-${String(index)}`}
									className="py-2 flex items-start justify-between gap-3"
								>
									<div className="min-w-0">
										<p className="text-sm text-default">
											{payment.date != null
												? formatStatementDate(payment.date)
												: t('-')}
										</p>
										<p className="text-xs text-brand-muted mt-0.5">
											{/* PD-16381 — a card by its funding ("Debit Card",
											    "Card"), never the stored 'Credit Card' type. */}
											{t(
												payment.paymentMethodType != null
													? paymentTypeLabel(
															payment.paymentMethodType,
															payment.paymentMethodCardCategory,
														)
													: 'Payment',
											)}
											{payment.paymentMethodLastFour != null &&
												` ${t(payment.paymentMethodLastFour)}`}
											{payment.feeCharged > 0 &&
												` · ${t('fee')} ${t(formatCurrency(payment.feeCharged) ?? '')}`}
										</p>
									</div>
									<div className="text-right shrink-0">
										<p className="text-sm font-medium text-default">
											{isCredit ? '-' : ''}
											{t(formatCurrency(payment.amountApplied) ?? '-')}
										</p>
										<p
											className={`text-xs mt-0.5 ${
												isFailed
													? 'text-danger font-medium'
													: 'text-brand-muted'
											}`}
										>
											{t(payment.displayStatus)}
										</p>
									</div>
								</div>
							);
						})}
					</div>
				</div>
			)}

			{/* Billing address */}
			{invoice.billingAddress != null && (
				<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
					<h2 className="text-sm font-semibold text-default mb-2">
						{t('Billing Address')}
					</h2>
					<div className="text-sm text-brand-muted">
						{invoice.billingAddress.name != null && (
							<p>{t(invoice.billingAddress.name)}</p>
						)}
						{invoice.billingAddress.line1 != null && (
							<p>{t(invoice.billingAddress.line1)}</p>
						)}
						{invoice.billingAddress.line2 != null && (
							<p>{t(invoice.billingAddress.line2)}</p>
						)}
						<p>
							{[invoice.billingAddress.city, invoice.billingAddress.state]
								.filter((v): v is string => v != null)
								.map((v) => t(v))
								.join(', ')}
							{invoice.billingAddress.zip != null &&
								` ${t(invoice.billingAddress.zip)}`}
						</p>
					</div>
				</div>
			)}

			{/* Line items */}
			<div className="bg-brand-surface border border-brand-border rounded-xl overflow-hidden">
				<h2 className="text-sm font-semibold text-default px-4 py-3 border-b border-brand-border">
					{t('Line Items')} ({t(String(invoice.lineItems.length))})
				</h2>
				<div className="divide-y divide-subtle">
					{invoice.lineItems.map((line) => (
						<button
							key={line.id}
							type="button"
							// Opens the receipt-style line detail (what was bought, at the
							// price paid) — not the live catalog product page. Works in every
							// storefront mode and for non-item lines (keyed on line.id).
							onClick={() => navigate(`/invoices/${id ?? ''}/line/${line.id}`)}
							className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-brand-input transition-colors"
						>
							{/* Thumbnail — same treatment as the order detail
							    line. Falls back to a generic image icon
							    when the item has no picture (or the line is
							    a non-item ad-hoc entry like a freight
							    surcharge); the row width still aligns with
							    rows that DO have pictures so the column
							    edges read cleanly. */}
							<div className="w-14 h-14 shrink-0 bg-brand-input rounded-lg flex items-center justify-center overflow-hidden">
								<ResilientImage
									src={line.primaryImageUrl}
									alt={t(line.description ?? '')}
									className="w-full h-full object-cover"
									onLikelyStaleUrl={() => {
										void queryClient.invalidateQueries({
											queryKey: ['invoice', invoiceId],
										});
									}}
									fallback={<ImagePlaceholder size="sm" />}
								/>
							</div>
							<div className="flex-1 min-w-0">
								<p className="text-sm font-medium text-default">
									{t(line.description ?? '')}
								</p>
								{line.itemExternalId != null && (
									<p className="text-xs text-faint mt-0.5">
										{t('Item')} #{t(line.itemExternalId)}
									</p>
								)}
								{/* Lot(s) the line shipped from (PD-15838). Only ERPs that
								    report lot allocations fill this, so the row is omitted
								    rather than shown blank. */}
								{line.lotNumber != null && line.lotNumber.length > 0 && (
									<p className="text-xs text-faint mt-0.5">
										{t('Lot')}: {t(line.lotNumber)}
									</p>
								)}
								{line.designations != null && (
									<div className="mt-0.5">
										<ItemDesignationBadges designations={line.designations} />
									</div>
								)}
								<p className="text-xs text-brand-muted mt-0.5">
									{t('Qty')}: {t(String(line.quantity ?? 0))} ×{' '}
									{t(formatCurrency(line.price) ?? '-')}
								</p>
							</div>
							<div className="text-sm font-medium text-default shrink-0">
								{t(formatCurrency(line.total) ?? '-')}
							</div>
							<CardChevron />
						</button>
					))}
				</div>
			</div>

			{/* Pay invoice sidebar. PD-15441 — never mounted for a credit
			    memo: its stored balance is positive on ERP-synced memos, so
			    the balance test alone let the sidebar mount behind a button
			    that is (correctly) hidden for one. */}
			{!isCreditMemo &&
				invoice.outstandingBalance != null &&
				parseFloat(invoice.outstandingBalance) > 0 && (
					<PayInvoiceSidebar
						isOpen={showPaySidebar}
						onClose={() => {
							setShowPaySidebar(false);
						}}
						invoiceId={invoice.id}
						outstandingBalance={parseFloat(invoice.outstandingBalance)}
						invoiceExternalId={displayExternalId(invoice, {
							fallback: 'none',
						})}
						// PD-15688 — a balance that already embeds a fee is charged
						// as-is, so the sidebar must not quote a second one.
						carriedProcessingFee={
							invoice.unpaidProcessingFee != null
								? invoice.unpaidProcessingFee
								: 0
						}
						onPaymentSuccess={() => {
							// The sidebar closes itself through `onClose` on success —
							// this only has to refresh the invoice's balance and status.
							void queryClient.invalidateQueries({
								queryKey: ['invoice', invoiceId],
							});
						}}
					/>
				)}
		</div>
	);
}
